import { useState, useMemo } from 'react';
import {
  Box,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  Typography
} from '@mui/material';
import { NormalizedOrder, OrdersResponse } from '../../types/unwinding.types';
import { PositionsGroupedByPair } from '../../types';

interface OrdersPanelProps {
  data: OrdersResponse | null | undefined;
  positions?: PositionsGroupedByPair[];
  isLoading?: boolean;
}

const timeAgo = (dateStr: string): string => {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 0) return 'now';
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  return `${Math.floor(seconds / 86400)}d`;
};

const fmtPrice = (v: number | null | undefined): string => {
  if (v == null || isNaN(v)) return '\u2014';
  if (v === 0) return '0.00';
  if (v >= 0.1) return v.toFixed(2);
  if (v >= 0.01) return v.toFixed(3);
  return v.toFixed(4);
};

const headerSx = {
  fontSize: '0.58rem', fontWeight: 600, color: 'grey.500',
  py: 0.2, px: 0.2, textTransform: 'uppercase' as const,
  letterSpacing: '0.02em', whiteSpace: 'nowrap' as const,
  borderBottom: '1px solid rgba(255,255,255,0.06)',
};

const cellSx = { fontSize: '0.72rem', py: 0.15, px: 0.15 } as const;

const statusColorMap: Record<string, 'default' | 'info' | 'warning'> = {
  resting: 'default',
  live: 'info',
  partial: 'warning',
};

/** Build a lookup map from marketId (ticker/tokenId) to market name + URL */
function buildMarketLookup(positions?: PositionsGroupedByPair[]): Map<string, { name: string; url?: string }> {
  const map = new Map<string, { name: string; url?: string }>();
  if (!positions) return map;
  for (const group of positions) {
    for (const pos of group.positions) {
      if (pos.marketId) {
        map.set(pos.marketId, {
          name: pos.platform === 'kalshi'
            ? (group.kalshiTitle || group.pairName)
            : (group.polymarketTitle || group.pairName),
          url: pos.marketUrl,
        });
      }
    }
  }
  return map;
}

export const OrdersPanel = ({ data, positions, isLoading }: OrdersPanelProps) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const marketLookup = useMemo(() => buildMarketLookup(positions), [positions]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <Typography sx={{ color: 'grey.500', fontSize: '0.8rem' }}>Loading orders...</Typography>
      </Box>
    );
  }

  const orders = data?.orders ?? [];
  const paginatedOrders = orders.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box>
      {/* Summary bar */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 1, py: 0.5 }}>
        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'white' }}>Orders</Typography>
        <Chip
          icon={<img src="/assets/exchanges/png/100x100/kalshi.png" style={{ width: 14, height: 14, borderRadius: '50%' }} />}
          label={`${data?.summary.kalshi.count ?? 0} orders \u00B7 $${(data?.summary.kalshi.totalVolume ?? 0).toFixed(0)}`}
          size="small" sx={{ fontSize: '0.65rem' }}
        />
        <Chip
          icon={<img src="/assets/exchanges/png/100x100/polymarket.png" style={{ width: 14, height: 14, borderRadius: '50%' }} />}
          label={`${data?.summary.polymarket.count ?? 0} orders \u00B7 $${(data?.summary.polymarket.totalVolume ?? 0).toFixed(0)}`}
          size="small" sx={{ fontSize: '0.65rem' }}
        />
        <Box sx={{ flex: 1 }} />
        {orders.length > 0 && (
          <Typography sx={{ fontSize: '0.6rem', color: 'grey.500' }}>{orders.length} total</Typography>
        )}
      </Stack>

      {orders.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography sx={{ color: 'grey.500', fontSize: '0.85rem' }}>No open orders</Typography>
        </Box>
      ) : (
        <>
          <TableContainer>
            <Table size="small" sx={{ tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headerSx, width: 30 }}>Plat</TableCell>
                  <TableCell sx={{ ...headerSx, width: 160 }}>Market</TableCell>
                  <TableCell sx={{ ...headerSx, width: 35 }} align="center">Side</TableCell>
                  <TableCell sx={{ ...headerSx, width: 35 }} align="center">Act</TableCell>
                  <TableCell sx={{ ...headerSx, width: 50 }} align="right">Price</TableCell>
                  <TableCell sx={{ ...headerSx, width: 50 }} align="right">Qty</TableCell>
                  <TableCell sx={{ ...headerSx, width: 40 }} align="right">Filled</TableCell>
                  <TableCell sx={{ ...headerSx, width: 55 }} align="center">Status</TableCell>
                  <TableCell sx={{ ...headerSx, width: 45 }} align="center">Src</TableCell>
                  <TableCell sx={{ ...headerSx, width: 40 }} align="right">Ago</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedOrders.map((order: NormalizedOrder) => (
                  <OrderRow key={order.id} order={order} marketLookup={marketLookup} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {orders.length > 25 && (
            <TablePagination
              component="div"
              count={orders.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
              rowsPerPageOptions={[25, 50, 100]}
              sx={{ '& .MuiTablePagination-toolbar': { minHeight: 28 }, '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.65rem' } }}
            />
          )}
        </>
      )}
    </Box>
  );
};

const OrderRow = ({ order, marketLookup }: { order: NormalizedOrder; marketLookup: Map<string, { name: string; url?: string }> }) => {
  const platformImg = order.platform === 'kalshi'
    ? '/assets/exchanges/png/100x100/kalshi.png'
    : '/assets/exchanges/png/100x100/polymarket.png';

  const market = marketLookup.get(order.marketId);
  const displayName = market?.name || order.marketId;
  const marketUrl = market?.url
    || (order.platform === 'kalshi' ? `https://kalshi.com/markets/${order.marketId}` : undefined);

  return (
    <TableRow sx={{ '&:hover': { backgroundColor: 'rgba(255,255,255,0.03)' } }}>
      <TableCell sx={cellSx}>
        <img src={platformImg} alt={order.platform} style={{ width: 14, height: 14, borderRadius: '50%' }} />
      </TableCell>

      <TableCell sx={{ ...cellSx, overflow: 'hidden' }}>
        <Tooltip title={`${displayName}\n${order.marketId}`} placement="top-start">
          {marketUrl ? (
            <a href={marketUrl} target="_blank" rel="noopener" style={{ textDecoration: 'none', color: 'inherit' }}>
              <Typography sx={{ fontSize: '0.65rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', '&:hover': { color: 'info.main' } }}>
                {displayName}
              </Typography>
            </a>
          ) : (
            <Typography sx={{ fontSize: '0.65rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayName}
            </Typography>
          )}
        </Tooltip>
      </TableCell>

      <TableCell sx={cellSx} align="center">
        <Chip label={order.side.toUpperCase()} size="small" color={order.side === 'yes' ? 'success' : 'error'}
          variant="outlined" sx={{ fontSize: '0.5rem', height: 16, '& .MuiChip-label': { px: 0.4 } }} />
      </TableCell>

      <TableCell sx={cellSx} align="center">
        <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: order.action === 'buy' ? 'success.light' : 'error.light' }}>
          {order.action.toUpperCase()}
        </Typography>
      </TableCell>

      <TableCell sx={cellSx} align="right">
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 600 }}>{fmtPrice(order.price)}</Typography>
      </TableCell>

      <TableCell sx={cellSx} align="right">
        <Typography sx={{ fontSize: '0.65rem' }}>{order.remainingQuantity}/{order.quantity}</Typography>
      </TableCell>

      <TableCell sx={cellSx} align="right">
        <Typography sx={{ fontSize: '0.65rem', color: order.filledQuantity > 0 ? 'warning.light' : 'grey.600' }}>
          {order.filledQuantity}
        </Typography>
      </TableCell>

      <TableCell sx={cellSx} align="center">
        <Chip label={order.status} size="small" color={statusColorMap[order.status] ?? 'default'}
          variant="outlined" sx={{ fontSize: '0.5rem', height: 16, '& .MuiChip-label': { px: 0.4 } }} />
      </TableCell>

      <TableCell sx={cellSx} align="center">
        {order.isUnwinding && (
          <Chip label="unwind" size="small" color="success"
            sx={{ fontSize: '0.45rem', height: 14, '& .MuiChip-label': { px: 0.3 } }} />
        )}
      </TableCell>

      <TableCell sx={cellSx} align="right">
        <Typography sx={{ fontSize: '0.6rem', color: 'grey.500' }}>{timeAgo(order.updatedAt)}</Typography>
      </TableCell>
    </TableRow>
  );
};
